import { cn, Text } from '@risksmart-app/atomic-ui';
import { useEffect, useRef, useState } from 'react';

import { OBJECT_TOOLBAR_TITLE_TEST_ID } from '../testIds';

/** Total crossfade duration; split evenly between the out and in phases. */
const FADE_MS = 150;
const HALF_MS = FADE_MS / 2;

interface Props {
  title: string;
}

/**
 * The object-toolbar title, crossfaded whenever it changes. Some registers
 * evict their query cache on tab navigation, which otherwise hard-flickers the
 * header (the title blanks, then the new value pops in). Instead this fades the
 * outgoing title out, swaps the text while it's invisible, then fades the new
 * one in — 75ms each way.
 *
 * The previous value is held on screen through the fade, and transient empty
 * titles (the momentary blank during a refetch) are ignored, so the header
 * never shows an empty gap.
 */
export const FadeTitle = ({ title }: Props) => {
  // `shown` is the title currently painted; it lags `title` by the fade-out
  // phase so the swap happens under cover of opacity 0.
  const [shown, setShown] = useState(title);
  const [visible, setVisible] = useState(true);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => {
    // Ignore no-ops and transient blanks — hold the last real title.
    if (!title || title === shown) {
      return;
    }
    setVisible(false);
    timer.current = setTimeout(() => {
      setShown(title);
      setVisible(true);
    }, HALF_MS);

    return () => clearTimeout(timer.current);
  }, [title, shown]);

  return (
    <Text
      preset={'heading-lg'}
      data-testid={OBJECT_TOOLBAR_TITLE_TEST_ID}
      className={cn(
        'min-w-0 truncate transition-opacity ease-out',
        visible ? 'opacity-100' : 'opacity-0'
      )}
      style={{ transitionDuration: `${HALF_MS}ms` }}
    >
      {shown}
    </Text>
  );
};
